/**
 * views/ResetPassword.tsx — /reset-password?token=<raw_token>
 * ─────────────────────────────────────────────────────────────
 * Unauthenticated route.  Validates the token from the URL query string,
 * then lets the user set a new password.  Token is consumed on first use.
 */

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient, ApiError } from '@/api/client';
import { InfiniumWordmark } from '@/components/ui/InfiniumLogo';

export default function ResetPassword() {
  const [params]                = useSearchParams();
  const token                   = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState<string | null>(null);

  if (!token) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-brand">
            <InfiniumWordmark variant="light" height={110} />
          </div>
          <div className="form-error" style={{ margin: '1.5rem 0' }}>
            Missing reset token. Please use the link from your email or
            contact your administrator.
          </div>
          <Link to="/" className="auth-back-link">Back to sign in</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await apiClient.post(
        '/api/auth/reset-password',
        { token, new_password: password },
        { skipAuth: true },
      );
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not complete the reset. Try requesting a new link.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-logo">
            <InfiniumWordmark variant="light" height={110} />
          </div>
          <h1 className="login-brand-name">Set New Password</h1>
          <p className="login-brand-sub">Choose a password with at least 8 characters</p>
        </div>

        {done ? (
          <div className="auth-sent-box">
            <div className="auth-sent-icon">✓</div>
            <p className="auth-sent-msg">Password updated successfully.</p>
            <Link to="/" className="login-submit-btn" style={{ textAlign: 'center', display: 'block' }}>
              Sign in
            </Link>
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="form-field">
              <label className="form-label" htmlFor="rp-pw">New password</label>
              <div className="pw-wrap">
                <input
                  id="rp-pw"
                  type={showPw ? 'text' : 'password'}
                  className="form-input"
                  autoComplete="new-password"
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={busy}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPw(v => !v)}
                  tabIndex={-1}
                >
                  {showPw ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="rp-confirm">Confirm password</label>
              <input
                id="rp-confirm"
                type={showPw ? 'text' : 'password'}
                className="form-input"
                autoComplete="new-password"
                placeholder="Repeat password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                disabled={busy}
                required
              />
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            <button
              type="submit"
              className="login-submit-btn"
              disabled={busy || !password || !confirm}
            >
              {busy ? 'Updating…' : 'Set new password'}
            </button>

            <Link to="/" className="auth-back-link">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
